{
	"title": "Settings",
	"subtitle": "Manage profile, security, notifications and subscription.",
	"savedJust": "Saved",
	"saving": "Saving…",
	"saveFailed": "Failed to save",
	"version": "Version {version}",
	"logout": "Log out",
	"logoutAll": "Log out everywhere",
	"logoutSuccess": "You have been logged out",
	"logoutAllSuccess": "All active sessions ended",
	"actions": {
		"cancel": "Cancel"
	},
	"readonly": {
		"banner": "Plan was downgraded — read-only mode. You can view and export, but not edit. Go to subscription →"
	},
	"grace": {
		"banner": "Account will be deleted in {days, plural, one {# day} other {# days}}",
		"restoreCta": "Cancel deletion",
		"restored": "Deletion cancelled. Account is active."
	},
	"nav": {
		"backToSettings": "Back to settings",
		"switchSection": "Switch section",
		"section": {
			"account": "Account",
			"communications": "Communications",
			"privacy": "Privacy"
		},
		"profile": "Profile",
		"account": "Account",
		"devices": "Devices",
		"appearance": "Appearance",
		"offline": "Offline",
		"notifications": "Notifications",
		"streak": "Learning streak",
		"privacy": "Privacy",
		"danger": "Danger zone",
		"billing": "Subscription",
		"desc": {
			"profile": "Name, avatar, education",
			"account": "Login, consents, sessions",
			"devices": "Active devices",
			"appearance": "Theme and term highlighting",
			"offline": "Data and audio on this device",
			"notifications": "Channels and event types",
			"streak": "Goal, reminder, freezes",
			"billing": "Plan, payment, quotas",
			"privacy": "Data and consents",
			"danger": "Account deletion",
			"language": "Interface and generation language"
		},
		"language": "Language"
	},
	"streak": {
		"title": "Learning streak",
		"subtitle": "Daily goal, reminder and streak auto-freeze.",
		"goalSection": "Daily goal",
		"goalLabel": "How much to study per day",
		"goalHelp": "The goal counts as met once you reach this many minutes of study in a day.",
		"goalOption": "{minutes} min/day",
		"reminderSection": "Reminder",
		"reminderLabel": "Reminder time",
		"reminderHelp": "If the goal isn't met by this hour, we'll send a reminder so you don't lose your streak.",
		"reminderOption": "{hour}:00",
		"tzNote": "Time zone: {tz}",
		"freezeSection": "Freezes",
		"autoFreezeLabel": "Auto-freeze",
		"autoFreezeHelp": "Automatically spend a freeze from your reserve so your streak survives a missed day. 1 is granted weekly.",
		"saveError": "Couldn't save"
	},
	"profile": {
		"title": "Profile",
		"avatar": "Avatar",
		"name": "Name",
		"email": "Email",
		"emailHelp": "Your account address — used for sign-in and notifications.",
		"autoGenerate": {
			"title": "Auto-generate materials",
			"description": "Off by default: topic materials are prepared by the course. This toggle only affects a single uploaded material.",
			"label": "Prepare materials on upload",
			"help": "When on, a note, flashcards, a quiz and a glossary are generated right after processing — for that one material, not for the course. Consumes your daily AI generation limit. When off, you can start any of them by hand from the material.",
			"offHint": "Off — the course prepares everything for its topics itself."
		},
		"timezone": "Timezone",
		"timezoneHelp": "Used for reminders and schedule.",
		"saved": "Profile saved",
		"saveError": "Failed to save profile",
		"saveHint": "Changes take effect right after saving.",
		"manageInIecId": "Manage in IEC ID",
		"nameManaged": "Your name comes from your IEC ID profile.",
		"avatarOwnHint": "By default your IEC ID profile photo is shown. You can set your own — for StudyHub only, your profile stays unchanged.",
		"avatarUpload": "Upload your own",
		"avatarReplace": "Replace photo",
		"avatarClear": "Remove your own",
		"avatarCleared": "Your photo removed — the profile photo is shown again",
		"avatarUploaded": "Photo updated",
		"avatarUploadError": "Could not upload the photo",
		"avatarErrors": {
			"invalidType": "JPEG, PNG or WebP required",
			"tooLarge": "File is larger than 5 MB"
		},
		"avatarCrop": {
			"title": "Crop avatar",
			"zoom": "Zoom",
			"apply": "Apply",
			"saving": "Uploading…"
		}
	},
	"account": {
		"title": "Account",
		"subtitle": "Manage login, security and consents.",
		"email": "Email",
		"registered": "Registered",
		"lastSeen": "Last seen",
		"consents": "Consents",
		"consentsHistory": "Show full history",
		"consentsLoadError": "Failed to load consent history",
		"consentsEmpty": "No consent history yet",
		"session": "Session",
		"logoutThis": "Log out of this device",
		"logoutAll": "Log out everywhere",
		"relatedActions": "Related actions:",
		"linkDevices": "Active devices",
		"linkDanger": "Delete account"
	},
	"devices": {
		"title": "Devices",
		"subtitle": "Sessions you are currently logged in with. End extra ones.",
		"current": "Current device",
		"lastActive": "Last active: {when}",
		"revoke": "End session",
		"revokeAll": "Log out everywhere",
		"revokeConfirmTitle": "End session?",
		"revokeConfirmBody": "Device «{device}» will be disconnected. Login will be required again.",
		"revokeAllConfirmTitle": "Log out of all devices?",
		"revokeAllConfirmBody": "All sessions including the current one will be ended. Re-login required.",
		"revokeSuccess": "Session ended",
		"revokeAllSuccess": "All sessions ended",
		"pushActive": "Push active",
		"pushNotConfigured": "Push: not configured",
		"notRecognizedHint": "Don't recognize a device? Log out everywhere and change your password.",
		"loadErrorTitle": "Failed to load devices",
		"loadErrorBody": "Check your connection and try again.",
		"retry": "Retry",
		"emptyTitle": "No active sessions",
		"emptyBody": "The device list appears once you sign in.",
		"columns": {
			"device": "Device",
			"location": "Location",
			"lastActive": "Active",
			"actions": ""
		}
	},
	"notifications": {
		"title": "Notifications",
		"subtitle": "Where and what StudyHub may notify you about.",
		"channels": "Delivery channels",
		"types": "Event types",
		"quietHours": "Quiet hours",
		"quietHoursDescription": "Don't push during the selected period.",
		"quietFrom": "From",
		"quietTo": "To",
		"channel": {
			"webPush": "Browser push",
			"mobilePush": "App push",
			"email": "Email",
			"inApp": "In app"
		},
		"channelHelp": {
			"webPushGranted": "This browser: allowed",
			"webPushDefault": "Tap to allow notifications in this browser",
			"webPushPermission": "This browser: allowed",
			"webPushDenied": "Browser blocked notifications. Open site settings.",
			"webPushUnsupported": "This browser doesn't support push notifications",
			"webPushNoVapid": "Browser push is temporarily unavailable",
			"mobilePushMobile": "This device: active",
			"mobilePushWeb": "Works in the StudyHub mobile app",
			"inApp": "Always works — disable for silence"
		},
		"webPushError": {
			"enabled": "Browser push enabled",
			"denied": "Notification permission denied. Enable it in your browser settings.",
			"unsupported": "This browser doesn't support push notifications.",
			"noVapid": "Browser push is unavailable right now. Try again later.",
			"error": "Couldn't enable browser push."
		},
		"type": {
			"aiReady": "AI: task ready (note, cards)",
			"srsReminder": "SRS reminders",
			"streaks": "Streaks (don't lose progress)",
			"quota": "Plan limits (90% used)",
			"subscription": "Subscription and payment",
			"security": "Security",
			"product": "Product news",
			"marketing": "Marketing (plans, promos)",
			"digest": "Weekly digest",
			"recommendations": "Recommendations",
			"quotaNoSale": "Usage limits (90% used)"
		},
		"securityLocked": "Always on — critical events",
		"tzNote": "Timezone: {tz} (from profile)",
		"saved": "Notifications saved",
		"saveError": "Failed to save notifications",
		"marketingRevoked": "Marketing emails are turned off in your IEC ID profile. You can re-enable them there — the choice applies across all services.",
		"srsTime": "Reminder time",
		"srsTimeHelp": "Push arrives at this time if any cards are due.",
		"typeHelp": {
			"aiReady": "Arrives when AI finishes a job: a note, flashcard set, quiz, audio recap or essay review is ready.",
			"srsReminder": "Daily reminder to review cards that are due. Sent only when something is actually due.",
			"streaks": "A nudge to study before your day streak breaks.",
			"quota": "Warning that 90% of your daily or monthly plan limit is used up.",
			"subscription": "Payment succeeded or failed, plan renewed, changed or about to expire.",
			"security": "Sign-in from a new device, login method change, account deletion request. Cannot be turned off.",
			"product": "Occasional emails about new StudyHub features and notable changes.",
			"marketing": "Promos, discounts and special plan offers. Does not affect service emails.",
			"digest": "Weekly summary: how much you studied, what you reviewed, where results dropped.",
			"recommendations": "Personal picks: what to review next, which gaps to close, which course to continue.",
			"quotaNoSale": "Warning that 90% of a daily or monthly usage limit is used up."
		}
	},
	"privacy": {
		"title": "Privacy",
		"subtitle": "How we use your data and what you can hide.",
		"consentsLoadError": "Failed to load consents",
		"consentsEmpty": "No consents yet",
		"aiTrainingNoticeTitle": "Materials privacy",
		"aiTrainingNotice": "Your materials are visible only to you and are used solely to generate notes, flashcards, quizzes and tutor answers based on your material. The AI provider processes them on-demand — prompts and responses are not retained on the model side.",
		"aiTrainingDisabled": "Private",
		"consentsAccepted": "Accepted consents",
		"dataUsage": "Data usage",
		"marketing": "Marketing emails",
		"learnMore": "Learn more",
		"dataExport": "Data export",
		"dataExportHeading": "Export my data",
		"dataExportIntro": "Get a machine-readable copy of your StudyHub data under GDPR Art. 20 (data portability).",
		"dataExportIncludesTitle": "Included in the archive:",
		"dataExportIncludes": "Profile, consents, materials, notes, flashcards, quizzes and attempts, tutor sessions, study plans, calendar, audio recaps, essays, visual analyses.",
		"dataExportExcludesTitle": "Not included:",
		"dataExportExcludes": "Original files (uploaded documents, audio and video recordings) — they stay available in the app until your account is deleted.",
		"dataExportCta": "Request data export",
		"dataExportPending": "Requesting…",
		"dataExportSuccess": "We'll email a download link to your inbox. The link is valid for 7 days.",
		"dataExportRateLimited": "You already requested an export recently. You can request a new one once a week.",
		"dataExportError": "Failed to request data export",
		"marketingMoved": "Managed in Notifications — together with the rest of your emails and pushes.",
		"cookieChoices": "Cookies and personalisation",
		"cookieChoicesHelp": "Your choice is kept in IEC ID and applies to all IEC services and apps.",
		"cookieChoiceOn": "On",
		"cookieChoiceOff": "Off",
		"cookieChoiceUndecided": "Not chosen",
		"cookieChoicesChange": "Change",
		"analyticsManageInIecId": "Manage cookies in IEC ID",
		"aiSharing": "Send data to AI services",
		"aiSharingHelp": "Needed for notes, flashcards, quizzes, the tutor and transcription. Data goes to Google (Gemini API) and, for web search, to Exa. When off, AI features don't work and nothing is sent.",
		"aiSharingDetails": "What is sent and to whom",
		"aiSharingOnToast": "AI features are on",
		"aiSharingOffToast": "AI features are off — nothing is sent to AI services"
	},
	"danger": {
		"title": "Danger zone",
		"subtitle": "Irreversible actions. Think twice.",
		"deleteHeading": "Delete account",
		"deleteIntro": "Soft deactivation first, then 30 days to cancel.",
		"whatHappens": "What happens:",
		"bullet1": "Account deactivates immediately — you log out of all devices",
		"bullet2": "30 days to cancel via email link or by signing in again",
		"bullet3": "After 30 days — all data is permanently deleted: materials, notes, cards, chat history, lecture recordings, essays, analytics",
		"bullet4": "Active subscription is not refunded",
		"bullet5": "Email frees up in 90 days",
		"bullet6": "Your IEC ID profile stays — only the StudyHub account is deleted",
		"deleteCta": "Delete account",
		"graceHeading": "Account will be deleted",
		"graceCountdownHint": "You can still cancel everything before final deletion.",
		"graceRequested": "Requested: {date}",
		"graceWipeAt": "Hard delete: {date}",
		"graceRemaining": "{days, plural, one {# day} other {# days}}",
		"graceRemainingLabel": "Remaining",
		"graceRequestedLabel": "Requested",
		"graceWipeLabel": "Deletion",
		"graceCanRestore": "You can cancel deletion right now:",
		"graceRestoreCta": "Cancel deletion",
		"graceRestoreViaEmail": "To cancel deletion, follow the link in the email sent to your inbox.",
		"graceAfterIrreversible": "After hard delete, data cannot be recovered.",
		"confirmTitle": "Delete account",
		"confirmBody": "To confirm, type the word «DELETE» in uppercase.",
		"confirmTypeWord": "DELETE",
		"confirmInputLabel": "Confirmation",
		"confirmCta": "Delete forever",
		"deletionRequested": "Deletion requested. A cancellation link has been emailed.",
		"restored": "Deletion cancelled. Account is active.",
		"deleteError": "Failed to request deletion"
	},
	"appearance": {
		"title": "Appearance",
		"subtitle": "Colour theme and term highlighting in texts.",
		"saveError": "Failed to save settings",
		"theme": "Theme",
		"themeLight": "Light",
		"themeDark": "Dark",
		"themeSystem": "System",
		"themeUseSystem": "Use system",
		"themeStatus": "Now: {resolved} (system: {system})",
		"themeSystemStatus": "Follows the system — currently: {resolved}"
	},
	"reading": {
		"title": "Reading",
		"subtitle": "How glossary terms behave in notes and transcripts.",
		"glossaryHighlight": {
			"title": "Highlight terms",
			"description": "Glossary terms get a dotted underline in notes, transcripts and tutor replies. Never in flashcards or quizzes."
		},
		"glossaryCourseOnly": {
			"title": "Only terms of the current course",
			"description": "Outside the course we highlight key terms only. Useful when the glossary grows large."
		},
		"saveError": "Failed to save reading settings"
	},
	"offline": {
		"title": "Offline",
		"subtitle": "What StudyHub keeps on this device.",
		"audioPrefetch": {
			"title": "Download audio ahead of time",
			"description": "As soon as a topic audio recap is ready it is downloaded to this device — listen offline."
		},
		"wifiOnly": {
			"title": "Wi-Fi only",
			"description": "Nothing is downloaded over mobile data. Data saver mode is always respected."
		},
		"usage": "Downloaded audio: {size}",
		"clear": "Clear",
		"cleared": "Audio cache cleared",
		"clearError": "Failed to clear the cache",
		"unavailable": "Works in the app and in browsers that support caching.",
		"saveError": "Failed to save offline settings",
		"localData": {
			"title": "Data on this device",
			"description": "Study data is downloaded automatically and works without a network.",
			"rowLabel": "Study database",
			"rowDescription": "Courses, notes, flashcards, quizzes, plans and calendar. Synced automatically — no need to clear it by hand."
		},
		"audio": {
			"title": "Audio recaps",
			"description": "Narrated topic recaps — the only thing kept outside the study database and downloaded separately."
		},
		"images": {
			"title": "Illustrations",
			"description": "Images from materials and notes — cached separately from the study database.",
			"usage": "Cached images: {size}",
			"hint": "Clearing this does not remove your notes or study data.",
			"cleared": "Image cache cleared",
			"clearError": "Failed to clear image cache"
		},
		"syncIssues": {
			"title": "Sync issues",
			"description": "Changes that could not be sent to the server. Resolve them here before they are forgotten.",
			"retry": "Retry",
			"open": "Open",
			"keepServer": "Keep server version",
			"keepServerDone": "Marked as resolved — server version kept",
			"copied": "Copied to clipboard",
			"actionError": "Action failed — try again",
			"reason": {
				"forbidden": "Not allowed",
				"invalid": "Invalid data",
				"conflict": "Conflict with server",
				"dropped": "Could not send after retries"
			},
			"entity": {
				"note": "Note {id}",
				"material": "Material {id}",
				"flashcardSet": "Flashcard set {id}",
				"item": "Item {id}",
				"studyPlan": "Study plan {id}",
				"recording": "Recording {id}",
				"calendarEvent": "Event {id}",
				"characterSession": "Character chat {id}"
			},
			"copyText": "Copy text",
			"dismiss": "Remove from list",
			"dismissDone": "Removed from the list — the server version is kept",
			"retrySent": "Changes sent to the server",
			"retryRejected": "The server rejected the change — it stays in the list",
			"retryOffline": "No connection — the change stays in the list",
			"retryNoSnapshot": "No snapshot of this change was saved — there is nothing to send",
			"noSnapshot": "Cannot be restored automatically — copy the text",
			"readOnlyHint": "This change does not need to be sent — the server already has the data. Remove it from the list."
		}
	},
	"billing": {
		"title": "Subscription",
		"subtitle": "Plan, payment methods and usage quotas.",
		"currentPlan": "Current plan",
		"manage": "Manage subscription",
		"details": "Plan details on the Subscription page.",
		"openBilling": "Go to subscription",
		"planLabel": "{tier} plan",
		"freeTagline": "Base plan — no charge",
		"cycleMonth": "mo",
		"cycleYear": "yr",
		"statusTrial": "Trial",
		"statusCancelling": "Cancelling",
		"renewsOn": "Renews: {date}",
		"trialUntil": "Trial until {date}",
		"endsOn": "Access until {date}",
		"upgradeCta": "Upgrade to Plus",
		"upgradeToPro": "Upgrade to Pro",
		"manageCta": "Manage subscription",
		"viewAllPlans": "All plans",
		"quotaTitle": "Usage quotas",
		"quota": {
			"notes": "Notes",
			"recordings": "Recordings",
			"audioRecaps": "Audio recaps"
		},
		"quotaUnlimited": "Unlimited",
		"quotaOf": "of {limit}",
		"compareLimits": "Compare plan limits",
		"paymentTitle": "Payment method",
		"paymentDefault": "Card",
		"paymentLast4": "•••• {last4}",
		"paymentManagedByProvider": "Managed by the payment provider",
		"paymentUpdate": "Update"
	},
	"profileMenu": {
		"profile": "Profile",
		"settings": "Settings",
		"billing": "Subscription and plan",
		"help": "Help and support",
		"replayOnboarding": "Replay onboarding",
		"legal": "Legal documents",
		"logout": "Log out",
		"tierFree": "Free",
		"tierPlus": "Plus",
		"tierPro": "Pro",
		"upsell": "Pro available",
		"appearance": "Appearance",
		"language": "Language",
		"manageAccount": "Manage account"
	},
	"language": {
		"title": "Language",
		"subtitle": "Interface language and the language AI generates materials in.",
		"section": "Language",
		"ui": "Interface language",
		"uiHelp": "Applies instantly. Your material texts stay in their original language.",
		"generation": "Generated materials language",
		"generationHelp": "AI creates notes, flashcards, quizzes, glossary, audio and visual explanations in this language. Uploaded files are still recognised in their original language.",
		"answerSameAsUi": "Interface language",
		"answerSourceLang": "Material language",
		"saveError": "Failed to save language settings"
	}
}
